// src/pages/wages/WagePayments.tsx
//
// Wage Payments — Single & Bulk payment, redesigned to match the card
// language used across the wages module (avatars, due badges, ledger
// palette) instead of plain native selects and boxed forms.
//
// Layout notes:
// - Single Payment is a two-column picker + form: a searchable worker list
//   on the left (selection state visible at a glance), and a receipt-style
//   payment form on the right once someone is picked.
// - Bulk Payment is a checklist of rich worker cards with a sticky
//   selection bar at the bottom, mirroring the sticky save bar pattern
//   used on the bulk attendance page.

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckSquare,
  Square,
  Wallet,
  User,
  Calendar,
  Clock,
  IndianRupee,
  Users,
  RefreshCw,
  Search,
  Check,
  Building2,
  AlertCircle,
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { ThrowError } from "../../api/callApi";
import { showToastNotification } from "../../utility/helper";
import { WageDueBadge } from "../../utility/wageDueStatus";

// ─── Types ────────────────────────────────────────────────────────────────

interface IPayableEmployee {
  id: string;
  name: string;
  designation?: string;
  site: { id: string; name: string };
  dailyRate: number;
  payCycleDays: number; // 10 | 15 | 30
  lastPaidDate: string; // ISO date (YYYY-MM-DD)
  daysWorkedSinceLastPaid: number; // fallback if we don't compute from dates
}

const inr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const daysBetween = (start: string, end: string): number => {
  const s = new Date(start);
  const e = new Date(end);
  const diff = (e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.floor(diff) + 1); // inclusive
};

// ─── Shared avatar chip ─────────────────────────────────────────────────

const Avatar: React.FC<{ name: string; size?: "sm" | "md" | "lg" }> = ({
  name,
  size = "md",
}) => {
  const sizes = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
  } as const;
  return (
    <div
      className={`${sizes[size]} rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0`}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// ─── Shared Confirmation Modal ─────────────────────────────────────────

interface IConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  items: Array<{
    name: string;
    days: number;
    rate: number;
    gross: number;
    advance?: number;
    net: number;
  }>;
  totalNet: number;
  onConfirm: () => void;
  isProcessing: boolean;
}

const PaymentConfirmationModal: React.FC<IConfirmationModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  items,
  totalNet,
  onConfirm,
  isProcessing,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl border-slate-200 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Wallet size={16} />
            </div>
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-slate-500">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="max-h-60 overflow-y-auto pr-1 -mr-1">
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-sm"
              >
                <div className="flex items-center gap-2.5">
                  <Avatar name={item.name} size="sm" />
                  <div>
                    <p className="font-medium text-slate-700 leading-tight">
                      {item.name}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {item.days}d × {inr(item.rate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {item.advance !== undefined && item.advance > 0 && (
                    <span className="text-xs text-rose-500">
                      -{inr(item.advance)}
                    </span>
                  )}
                  <span className="font-semibold text-primary tabular-nums">
                    {inr(item.net)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-600">Total</span>
          <span className="text-xl font-bold text-primary tabular-nums">
            {inr(totalNet)}
          </span>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isProcessing}
            className="rounded-xl"
          >
            {isProcessing ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Wallet className="h-4 w-4 mr-2" /> Confirm Payment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Single Payment: worker picker + payment form ──────────────────────

interface ISinglePaymentProps {
  employees: IPayableEmployee[];
  preselectedId?: string | null;
  onPaymentComplete: (paidIds: string[]) => void;
}

const SinglePayment: React.FC<ISinglePaymentProps> = ({
  employees,
  preselectedId,
  onPaymentComplete,
}) => {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [daysWorked, setDaysWorked] = useState<number>(0);
  const [advance, setAdvance] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Seed selection: preselected employee (from ?employeeId=) if valid, else first.
  useEffect(() => {
    if (!employees.length) {
      setSelectedId("");
      return;
    }
    if (preselectedId && employees.some((e) => e.id === preselectedId)) {
      setSelectedId(preselectedId);
    } else if (!employees.some((e) => e.id === selectedId)) {
      setSelectedId(employees[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, preselectedId]);

  const selectedEmployee = employees.find((e) => e.id === selectedId);

  // When the selected worker changes, default the period to
  // (lastPaidDate + 1) through (start + payCycleDays - 1).
  useEffect(() => {
    if (!selectedEmployee) return;
    const last = new Date(selectedEmployee.lastPaidDate);
    last.setDate(last.getDate() + 1);
    const start = last.toISOString().split("T")[0];
    const endDateObj = new Date(start);
    endDateObj.setDate(
      endDateObj.getDate() + selectedEmployee.payCycleDays - 1,
    );
    const end = endDateObj.toISOString().split("T")[0];
    setStartDate(start);
    setEndDate(end);
    setDaysWorked(daysBetween(start, end));
    setAdvance(0);
  }, [selectedEmployee?.id]);

  const handleStartChange = (val: string) => {
    setStartDate(val);
    setDaysWorked(daysBetween(val, endDate));
  };

  const handleEndChange = (val: string) => {
    setEndDate(val);
    setDaysWorked(daysBetween(startDate, val));
  };

  const autoFillFromCycle = () => {
    if (!selectedEmployee || !startDate) return;
    const endObj = new Date(startDate);
    endObj.setDate(endObj.getDate() + selectedEmployee.payCycleDays - 1);
    const end = endObj.toISOString().split("T")[0];
    setEndDate(end);
    setDaysWorked(daysBetween(startDate, end));
  };

  const gross = (selectedEmployee?.dailyRate || 0) * daysWorked;
  const net = gross - advance;

  const filteredEmployees = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.site.name.toLowerCase().includes(q),
    );
  }, [employees, search]);

  const handleConfirm = async () => {
    if (!selectedEmployee) return;
    setIsProcessing(true);
    try {
      // TODO: WageService.recordPayment({ employeeId, daysWorked, advance, periodStart: startDate, periodEnd: endDate })
      await new Promise((resolve) => setTimeout(resolve, 1000));
      showToastNotification(
        "success",
        `Paid ${inr(net)} to ${selectedEmployee.name} (${startDate} → ${endDate})`,
      );
      onPaymentComplete([selectedEmployee.id]);
      setShowModal(false);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const modalItems = selectedEmployee
    ? [
        {
          name: selectedEmployee.name,
          days: daysWorked,
          rate: selectedEmployee.dailyRate,
          gross,
          advance,
          net,
        },
      ]
    : [];

  if (employees.length === 0) {
    return (
      <div className="bg-white border border-slate-200/70 rounded-2xl shadow-sm py-16 text-center">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
        <p className="text-sm font-medium text-slate-500">
          No workers due for payment
        </p>
        <p className="text-xs text-slate-400 mt-0.5">
          Everyone's up to date right now.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 items-start">
      {/* Worker picker */}
      <div className="bg-white border border-slate-200/70 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search worker or site..."
              className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 bg-slate-50/60 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
          </div>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-slate-50">
          {filteredEmployees.length === 0 ? (
            <p className="text-center text-xs text-slate-400 py-8">
              No matches
            </p>
          ) : (
            filteredEmployees.map((e) => {
              const isSelected = e.id === selectedId;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelectedId(e.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                    isSelected ? "bg-primary/5" : "hover:bg-slate-50"
                  }`}
                >
                  <Avatar name={e.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-700 truncate">
                      {e.name}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate">
                      {e.site.name}
                    </p>
                  </div>
                  {isSelected ? (
                    <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Check size={12} className="text-white" />
                    </div>
                  ) : (
                    <WageDueBadge
                      lastPaidDate={e.lastPaidDate}
                      cycleDays={e.payCycleDays}
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Payment form */}
      {selectedEmployee && (
        <div className="bg-white border border-slate-200/70 rounded-2xl shadow-sm overflow-hidden">
          {/* Worker header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/50">
            <Avatar name={selectedEmployee.name} size="lg" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-800 truncate">
                {selectedEmployee.name}
              </p>
              <p className="text-xs text-slate-400 flex items-center gap-1.5 truncate">
                {selectedEmployee.designation || "—"}
                <span className="text-slate-300">·</span>
                <Building2 size={11} className="shrink-0" />{" "}
                {selectedEmployee.site.name}
              </p>
            </div>
            <WageDueBadge
              lastPaidDate={selectedEmployee.lastPaidDate}
              cycleDays={selectedEmployee.payCycleDays}
            />
          </div>

          <div className="p-5 space-y-5">
            <div className="flex items-center justify-between text-xs bg-slate-50 rounded-xl px-4 py-2.5 border border-slate-100 text-slate-500">
              <span>Last paid</span>
              <span className="font-medium text-slate-700">
                {selectedEmployee.lastPaidDate}
              </span>
              <span className="text-slate-300">|</span>
              <span>Daily rate</span>
              <span className="font-medium text-slate-700">
                {inr(selectedEmployee.dailyRate)}
              </span>
            </div>

            {/* Payment Period */}
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Calendar size={16} className="text-slate-400" />
                Payment period
              </label>
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartChange(e.target.value)}
                  className="flex-1 min-w-0 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-slate-50/50"
                />
                <span className="text-slate-300 shrink-0">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndChange(e.target.value)}
                  className="flex-1 min-w-0 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-slate-50/50"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={autoFillFromCycle}
                  className="rounded-xl shrink-0"
                  title="Auto-fill end date based on pay cycle"
                >
                  <RefreshCw size={13} />
                </Button>
              </div>
            </div>

            {/* Days & Advance */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Clock size={16} className="text-slate-400" />
                  Days worked
                </label>
                <input
                  type="number"
                  min={0}
                  value={daysWorked}
                  onChange={(e) => setDaysWorked(Number(e.target.value))}
                  className="mt-1.5 w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-slate-50/50"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <IndianRupee size={16} className="text-slate-400" />
                  Advance deduction
                </label>
                <input
                  type="number"
                  min={0}
                  value={advance}
                  onChange={(e) => setAdvance(Number(e.target.value))}
                  className="mt-1.5 w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-slate-50/50"
                />
              </div>
            </div>

            {/* Receipt-style breakdown */}
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>
                  Gross wage ({daysWorked}d × {inr(selectedEmployee.dailyRate)})
                </span>
                <span className="tabular-nums">{inr(gross)}</span>
              </div>
              {advance > 0 && (
                <div className="flex justify-between text-slate-500">
                  <span>Advance deduction</span>
                  <span className="text-rose-500 tabular-nums">
                    - {inr(advance)}
                  </span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-slate-900 text-base pt-2 mt-1 border-t border-slate-200">
                <span>Net payable</span>
                <span className="text-primary tabular-nums">{inr(net)}</span>
              </div>
            </div>

            <Button
              onClick={() => setShowModal(true)}
              disabled={net <= 0}
              className="w-full rounded-xl h-11"
            >
              <Wallet className="h-4 w-4 mr-2" /> Pay {inr(Math.max(net, 0))}
            </Button>
          </div>
        </div>
      )}

      <PaymentConfirmationModal
        open={showModal}
        onOpenChange={setShowModal}
        title="Confirm single payment"
        description={
          selectedEmployee
            ? `Paying ${selectedEmployee.name} for ${daysWorked} days (${startDate} → ${endDate}).`
            : undefined
        }
        items={modalItems}
        totalNet={net}
        onConfirm={handleConfirm}
        isProcessing={isProcessing}
      />
    </div>
  );
};

// ─── Bulk Payment: checklist + sticky selection bar ─────────────────────

interface IBulkPaymentProps {
  employees: IPayableEmployee[];
  onPaymentComplete: (paidIds: string[]) => void;
}

const BulkPayment: React.FC<IBulkPaymentProps> = ({
  employees,
  onPaymentComplete,
}) => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const filteredEmployees = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.site.name.toLowerCase().includes(q),
    );
  }, [employees, search]);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    const visibleIds = filteredEmployees.map((e) => e.id);
    const allVisibleSelected = visibleIds.every((id) =>
      selectedIds.includes(id),
    );
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const selectedEmployees = employees.filter((e) => selectedIds.includes(e.id));
  const totalNet = selectedEmployees.reduce(
    (sum, e) => sum + e.dailyRate * e.daysWorkedSinceLastPaid,
    0,
  );

  const handleConfirm = async () => {
    if (!selectedIds.length) return;
    setIsProcessing(true);
    try {
      // TODO: WageService.recordBulkPayment({ employeeIds: selectedIds })
      await new Promise((resolve) => setTimeout(resolve, 1000));
      showToastNotification(
        "success",
        `Paid ${selectedIds.length} worker(s) — ${inr(totalNet)} total`,
      );
      onPaymentComplete(selectedIds);
      setSelectedIds([]);
      setShowModal(false);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const modalItems = selectedEmployees.map((e) => ({
    name: e.name,
    days: e.daysWorkedSinceLastPaid,
    rate: e.dailyRate,
    gross: e.dailyRate * e.daysWorkedSinceLastPaid,
    net: e.dailyRate * e.daysWorkedSinceLastPaid,
  }));

  const allVisibleSelected =
    filteredEmployees.length > 0 &&
    filteredEmployees.every((e) => selectedIds.includes(e.id));

  return (
    <div className="space-y-4 pb-24">
      <div className="bg-white border border-slate-200/70 rounded-2xl shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-slate-100">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search worker or site..."
              className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 bg-slate-50/60 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
          </div>
          <button
            onClick={toggleAll}
            disabled={filteredEmployees.length === 0}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-primary transition-colors disabled:opacity-40 disabled:hover:text-slate-600"
          >
            {allVisibleSelected ? (
              <CheckSquare size={18} className="text-primary" />
            ) : (
              <Square size={18} className="text-slate-400" />
            )}
            {allVisibleSelected ? "Deselect all" : "Select all"}
          </button>
          <span className="text-xs text-slate-400 sm:ml-auto">
            {selectedIds.length} of {employees.length} selected
          </span>
        </div>

        {/* Worker checklist */}
        <div className="divide-y divide-slate-50 max-h-[520px] overflow-y-auto">
          {filteredEmployees.length === 0 ? (
            <div className="py-14 text-center text-slate-400">
              <AlertCircle className="h-7 w-7 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">
                No workers found
              </p>
            </div>
          ) : (
            filteredEmployees.map((e) => {
              const isSelected = selectedIds.includes(e.id);
              const gross = e.dailyRate * e.daysWorkedSinceLastPaid;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => toggleSelected(e.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    isSelected ? "bg-primary/5" : "hover:bg-slate-50/80"
                  }`}
                >
                  {isSelected ? (
                    <CheckSquare size={18} className="text-primary shrink-0" />
                  ) : (
                    <Square size={18} className="text-slate-300 shrink-0" />
                  )}
                  <Avatar name={e.name} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-700 truncate">
                      {e.name}
                    </p>
                    <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                      <Building2 size={11} className="shrink-0" /> {e.site.name}
                      <span className="text-slate-300">·</span>
                      {e.daysWorkedSinceLastPaid} days
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-slate-700 tabular-nums">
                      {inr(gross)}
                    </p>
                    <WageDueBadge
                      lastPaidDate={e.lastPaidDate}
                      cycleDays={e.payCycleDays}
                    />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Sticky selection bar */}
      <div className="fixed bottom-0 left-0 right-0 sm:left-[var(--sidebar-width,0px)] z-20 border-t border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
        <div className="max-w-full flex items-center justify-between gap-3 px-6 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Users size={15} className="text-slate-400" />
            <span className="text-slate-500">
              {selectedIds.length} worker{selectedIds.length === 1 ? "" : "s"}{" "}
              selected
            </span>
            {selectedIds.length > 0 && (
              <span className="font-semibold text-primary tabular-nums ml-1">
                {inr(totalNet)}
              </span>
            )}
          </div>
          <Button
            onClick={() => setShowModal(true)}
            disabled={!selectedIds.length}
            className="gap-2 rounded-xl"
          >
            <Wallet size={16} />
            Pay {selectedIds.length || ""} worker
            {selectedIds.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>

      <PaymentConfirmationModal
        open={showModal}
        onOpenChange={setShowModal}
        title="Confirm bulk payment"
        description={`Paying ${selectedIds.length} worker(s) for their due wages.`}
        items={modalItems}
        totalNet={totalNet}
        onConfirm={handleConfirm}
        isProcessing={isProcessing}
      />
    </div>
  );
};

// ─── Main WagePayments Page ─────────────────────────────────────────────

const WagePayments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get("employeeId");

  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState<IPayableEmployee[]>([]);
  const [activeTab, setActiveTab] = useState<"single" | "bulk">(
    preselectedId ? "single" : "bulk",
  );

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const mockData: IPayableEmployee[] = [
        {
          id: "1",
          name: "Ramesh Kumar",
          designation: "Mason",
          site: { id: "s1", name: "Site 4 — Roofing" },
          dailyRate: 650,
          payCycleDays: 15,
          lastPaidDate: "2026-08-08",
          daysWorkedSinceLastPaid: 13,
        },
        {
          id: "2",
          name: "Sunita Devi",
          designation: "Helper",
          site: { id: "s2", name: "Site 2 — Block A" },
          dailyRate: 600,
          payCycleDays: 10,
          lastPaidDate: "2026-08-21",
          daysWorkedSinceLastPaid: 2,
        },
        {
          id: "3",
          name: "Arvind Yadav",
          designation: "Mason",
          site: { id: "s1", name: "Site 4 — Roofing" },
          dailyRate: 650,
          payCycleDays: 30,
          lastPaidDate: "2026-07-25",
          daysWorkedSinceLastPaid: 24,
        },
        {
          id: "4",
          name: "Priya Sharma",
          designation: "Supervisor",
          site: { id: "s4", name: "Site 3 — Interior" },
          dailyRate: 750,
          payCycleDays: 15,
          lastPaidDate: "2026-08-01",
          daysWorkedSinceLastPaid: 14,
        },
      ];
      setEmployees(mockData);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (preselectedId && employees.some((e) => e.id === preselectedId)) {
      setActiveTab("single");
    }
  }, [employees, preselectedId]);

  const handlePaymentComplete = (paidIds: string[]) => {
    setEmployees((prev) => prev.filter((e) => !paidIds.includes(e.id)));
  };

  const totalDue = useMemo(
    () =>
      employees.reduce(
        (sum, e) => sum + e.dailyRate * e.daysWorkedSinceLastPaid,
        0,
      ),
    [employees],
  );
  const overdueCount = useMemo(
    () =>
      employees.filter((e) => e.daysWorkedSinceLastPaid >= e.payCycleDays)
        .length,
    [employees],
  );

  return (
    <div className="space-y-6 pb-4">
      <MainHeading title="Wage Payments" />

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200/70 rounded-xl p-4 shadow-sm flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
            <Users size={17} />
          </div>
          <div>
            <p className="text-xs text-slate-400">Workers due</p>
            <p className="text-xl font-bold text-slate-800 tabular-nums">
              {employees.length}
            </p>
          </div>
        </div>
        <div className="bg-white border border-slate-200/70 rounded-xl p-4 shadow-sm flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500 shrink-0">
            <AlertCircle size={17} />
          </div>
          <div>
            <p className="text-xs text-slate-400">Overdue</p>
            <p className="text-xl font-bold text-slate-800 tabular-nums">
              {overdueCount}
            </p>
          </div>
        </div>
        <div className="bg-white border border-slate-200/70 rounded-xl p-4 shadow-sm flex items-center gap-3 col-span-2 sm:col-span-1">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <IndianRupee size={17} />
          </div>
          <div>
            <p className="text-xs text-slate-400">Total payable</p>
            <p className="text-xl font-bold text-primary tabular-nums">
              {inr(totalDue)}
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-16 bg-slate-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "single" | "bulk")}
          className="w-full"
        >
          <TabsList className="bg-slate-100/80 flex py-5 gap-2 rounded-xl border border-slate-200/60 shadow-inner mb-6 w-fit">
            <TabsTrigger
              className="rounded-lg py-3 px-6 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary border border-transparent font-semibold text-slate-500 transition-all"
              value="single"
            >
              <User size={16} className="mr-2" />
              Single Payment
            </TabsTrigger>
            <TabsTrigger
              className="rounded-lg py-3 px-6 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary border border-transparent font-semibold text-slate-500 transition-all"
              value="bulk"
            >
              <Users size={16} className="mr-2" />
              Bulk Payment
            </TabsTrigger>
          </TabsList>

          <TabsContent value="single">
            <SinglePayment
              employees={employees}
              preselectedId={preselectedId}
              onPaymentComplete={handlePaymentComplete}
            />
          </TabsContent>

          <TabsContent value="bulk">
            <BulkPayment
              employees={employees}
              onPaymentComplete={handlePaymentComplete}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default WagePayments;
